import { useEffect, useMemo, useState } from "react";
import facilityService from "../services/facilityService";
import { FACILITY_STATUS } from "../constants/facility.constants";

// Loads the facility list and applies the search box + status tab client-side.
// The backend returns the full list in one call (same as v1), so filtering
// here avoids adding new list endpoints.
export function useFacilities() {
  const [facilities, setFacilities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(FACILITY_STATUS.ALL);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const loadFacilities = async () => {
    setIsLoading(true);
    try {
      const data = await facilityService.getFacilities();
      const sorted = [...data].sort((a, b) => a.facility_number - b.facility_number);
      setFacilities(sorted);
    } catch (error) {
      console.error("Error fetching facilities:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFacilities();
  }, []);

  const filteredFacilities = useMemo(() => {
    const query = search.trim().toLowerCase();

    return facilities.filter((facility) => {
      const matchesStatus =
        status === FACILITY_STATUS.ALL ||
        (status === FACILITY_STATUS.ACTIVE && facility.is_active) ||
        (status === FACILITY_STATUS.INACTIVE && !facility.is_active);

      if (!matchesStatus) return false;
      if (!query) return true;

      const haystack = [facility.name, facility.primary_domain, facility.address]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [facilities, search, status]);

  // Jump back to page 1 whenever the search/status filters narrow the list,
  // so the user never lands on a page that no longer has any rows.
  useEffect(() => {
    setPage(1);
  }, [search, status]);

  const total = filteredFacilities.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const paginatedFacilities = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredFacilities.slice(start, start + limit);
  }, [filteredFacilities, page, limit]);

  return {
    facilities: paginatedFacilities,
    total,
    isLoading,
    search,
    setSearch,
    status,
    setStatus,
    page,
    setPage,
    limit,
    setLimit,
    totalPages,
    refetch: loadFacilities,
  };
}
